import { GAMIFICATION_LEVEL_COUNT } from '@communities/common-layer/constants'
import type {
  FormatGamificationLevelLabelKeys,
  GamificationLevelRow,
  GamificationLevelTranslate,
  PendingAwareQuery,
} from '@communities/common-layer/types/gamification.types'

export interface LevelIds {
  groupId?: string
  locationId?: string
}

/** TanStack v4 exposes `isLoading`, v5 exposes `isPending`; read whichever exists. */
export function isQueryPending(query: PendingAwareQuery): boolean {
  return query.isPending?.value ?? query.isLoading?.value ?? false
}

export function defaultLevelLabel(level: number): string {
  return `Level ${level}`
}

export function getMinPointsForLevel(level: number, rows?: GamificationLevelRow[]): number {
  if (rows !== undefined) {
    return rows.find(entry => entry.level === level)?.minPoints ?? 0
  }

  return 0
}

export function isMaxLevel(level: number): boolean {
  return level >= GAMIFICATION_LEVEL_COUNT
}

/**
 * Read gate for GET/cache/override paths: exactly nine rows, levels 1–9 once each,
 * integer minPoints, level-1 at 0, strictly increasing thresholds, and string|null names.
 * Name length and other admin-only save rules stay on `validateGamificationLevels` before PUT.
 */
export function isCompleteLevelsDocument(levels: unknown): levels is GamificationLevelRow[] {
  if (!Array.isArray(levels) || levels.length !== GAMIFICATION_LEVEL_COUNT) {
    return false
  }

  const seenLevels = new Set<number>()
  const rows: GamificationLevelRow[] = []

  for (const entry of levels) {
    if (!entry || typeof entry !== 'object') {
      return false
    }

    const row = entry as Partial<GamificationLevelRow>
    const level = row.level
    const minPoints = row.minPoints
    const name = row.name

    if (
      typeof level !== 'number' ||
      typeof minPoints !== 'number' ||
      !Number.isInteger(level) ||
      level < 1 ||
      level > GAMIFICATION_LEVEL_COUNT ||
      seenLevels.has(level) ||
      !Number.isInteger(minPoints) ||
      !Number.isFinite(minPoints) ||
      minPoints < 0 ||
      (name !== null && name !== undefined && typeof name !== 'string')
    ) {
      return false
    }

    if (level === 1 && minPoints !== 0) {
      return false
    }

    seenLevels.add(level)
    rows.push({ level, name: name ?? null, minPoints })
  }

  if (seenLevels.size !== GAMIFICATION_LEVEL_COUNT) {
    return false
  }

  rows.sort((left, right) => left.level - right.level)
  for (let index = 1; index < rows.length; index += 1) {
    if (rows[index].minPoints <= rows[index - 1].minPoints) {
      return false
    }
  }

  return true
}

export function isCustomLevelName(name: string, level: number): boolean {
  const trimmed = name.trim()
  return Boolean(trimmed) && trimmed !== defaultLevelLabel(level)
}

export function getCustomLevelName(name: string | null | undefined, level: number): string | undefined {
  const trimmed = (name ?? '').trim()
  return isCustomLevelName(trimmed, level) ? trimmed : undefined
}

export function formatGamificationLevelLabel(
  t: GamificationLevelTranslate,
  keys: FormatGamificationLevelLabelKeys,
  level: number,
  rowName: string | null | undefined
): string {
  const customName = getCustomLevelName(rowName, level)
  if (customName) {
    return t(keys.levelWithName, { level, name: customName })
  }
  return t(keys.levelOnly, { level })
}

/** Treat empty / whitespace as missing. Exported so composables reuse one rule. */
export function normalizeId(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed || undefined
}

/**
 * Resolve groupId + locationId for the levels GET.
 * Prefer `primary` (membership / badge / page); use `fallback` only for missing fields
 * (e.g. feed popover → current group when the embed omits ids).
 */
export function pickLevelIds(primary: LevelIds, fallback?: LevelIds): LevelIds {
  return {
    groupId: normalizeId(primary.groupId) ?? normalizeId(fallback?.groupId),
    locationId: normalizeId(primary.locationId) ?? normalizeId(fallback?.locationId),
  }
}

/** Complete ladder + transport error → keep showing ladder. Otherwise: transport error → failed;
 * settled with an incomplete payload → failed. (Pending with no data → not failed.) */
export function isLevelsFailed(levels: GamificationLevelRow[] | undefined, isTransportError: boolean, isPending: boolean): boolean {
  if (isCompleteLevelsDocument(levels)) {
    return false
  }
  return isTransportError || (levels !== undefined && !isPending)
}

/** Complete nine-row document or `[]` (never pad partials with defaults). */
export function toCompleteLevels(levels: GamificationLevelRow[] | undefined): GamificationLevelRow[] {
  return isCompleteLevelsDocument(levels) ? levels : []
}

/** Props-down complete document wins; else fetched complete list or `undefined`. */
export function pickLevels(
  explicit: GamificationLevelRow[] | undefined,
  fetched: GamificationLevelRow[]
): GamificationLevelRow[] | undefined {
  if (isCompleteLevelsDocument(explicit)) {
    return explicit
  }
  return fetched.length > 0 ? fetched : undefined
}

/** TanStack v5 keeps disabled queries "pending" — only load while a fetch can run. */
export function isLevelsPending(canFetch: boolean, hasLevels: boolean, isPending: boolean): boolean {
  return canFetch && !hasLevels && isPending
}

/** Group ids whose levels-load failure already toasted this session. */
const shownFor = new Set<string>()

/**
 * Cross-instance toast dedupe: a feed of 20 avatar popovers must not fire 20 identical
 * "levels load failed" toasts when the shared levels query errors.
 * Returns true the first time a group id is marked (caller should toast), false after.
 */
export function markLevelsErrorToastShown(groupId: string): boolean {
  if (shownFor.has(groupId)) {
    return false
  }
  shownFor.add(groupId)
  return true
}

/** Test hook — clears the session dedupe set. */
export function resetLevelsErrorToastDedupe(): void {
  shownFor.clear()
}
